import React, { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { X, Upload, AlertCircle, CheckCircle2, FileSpreadsheet } from "lucide-react";

export default function ImportRulesModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dryRun ? "true" : "false");
    try {
      const { data } = await api.post("/pricing-rules/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { dry_run: dryRun },
      });
      if (dryRun) setPreview(data); else setResult(data);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(7, 9, 26, 0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="surface w-full max-h-[88vh] flex flex-col"
        style={{ maxWidth: 720, background: "var(--surface-1)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="import-modal"
      >
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md" style={{ background: "var(--gradient-accent-soft)", color: "var(--accent)" }}>
              <FileSpreadsheet className="w-4 h-4" />
            </div>
            <div>
              <div className="eyebrow-accent mb-1">Bulk Import</div>
              <h2 className="font-display text-xl font-bold">Import Pricing Rules</h2>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-icon"><X /></button>
        </div>

        <div className="p-6 flex-1 overflow-auto space-y-5">
          {!result && (
            <>
              <div className="surface-inset p-4 text-xs leading-relaxed">
                <div className="label-eyebrow mb-2">Expected Columns</div>
                <div className="font-mono text-[11px] mb-2" style={{ color: "var(--text-secondary)" }}>
                  category · item_name · description · unit · cost · sell · default_labour_hours · default_wastage_pct · supplier
                </div>
                <div style={{ color: "var(--text-muted)" }}>
                  Matching is by <b style={{ color: "var(--text-primary)" }}>(category, item_name)</b>. Existing rows are updated, new rows are created.
                  Flexible headers like <code>Cost Price</code>, <code>Sell Price</code>, <code>Item Name</code>, <code>Wastage %</code> are auto-mapped.
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">File (.csv or .xlsx)</label>
                <div
                  className="surface-inset p-6 flex flex-col items-center gap-3 cursor-pointer transition-colors"
                  style={{
                    borderStyle: "dashed",
                    borderColor: file ? "var(--accent)" : "var(--border-strong)",
                  }}
                  onClick={() => document.getElementById("pq-import-file").click()}
                >
                  <Upload className="w-6 h-6" style={{ color: file ? "var(--accent)" : "var(--text-muted)" }} />
                  <div className="text-sm" style={{ color: "var(--text-primary)" }}>
                    {file ? file.name : "Click to select a file"}
                  </div>
                  {file && (
                    <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  )}
                  <input
                    id="pq-import-file"
                    type="file"
                    accept=".csv,.xlsx,.xlsm"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                    data-testid="import-file-input"
                    className="hidden"
                  />
                </div>
              </div>

              {preview && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "var(--success)" }}>
                    <CheckCircle2 className="w-4 h-4" /> Preview ready
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <Stat label="Total rows" value={preview.total_rows} />
                    <Stat label="Valid" value={preview.valid} />
                    <Stat label="Will create" value={preview.would_create} accent />
                    <Stat label="Will update" value={preview.would_update} accent />
                  </div>
                  {preview.errors?.length > 0 && (
                    <div
                      className="text-xs p-3 rounded"
                      style={{
                        background: "var(--danger-soft)",
                        color: "#FECDD3",
                        border: "1px solid rgba(251,113,133,0.30)",
                      }}
                    >
                      <div className="font-bold mb-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> {preview.errors.length} row error(s)
                      </div>
                      <ul className="list-disc ml-5 space-y-0.5">
                        {preview.errors.slice(0, 5).map((e, i) => (
                          <li key={i}>Row {e.row}: {e.message}</li>
                        ))}
                        {preview.errors.length > 5 && <li>… {preview.errors.length - 5} more</li>}
                      </ul>
                    </div>
                  )}
                  <details className="text-xs surface-inset rounded p-3">
                    <summary className="cursor-pointer" style={{ color: "var(--text-secondary)" }}>Show first 10 rows</summary>
                    <div className="mt-3 max-h-56 overflow-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr style={{ color: "var(--text-muted)" }}>
                            <th className="text-left px-2 py-1 font-mono">Category</th>
                            <th className="text-left px-2 py-1 font-mono">Item</th>
                            <th className="text-right px-2 py-1 font-mono">Cost</th>
                            <th className="text-right px-2 py-1 font-mono">Sell</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.preview.slice(0, 10).map((p, i) => (
                            <tr key={i} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                              <td className="px-2 py-1" style={{ color: "var(--text-muted)" }}>{p.category}</td>
                              <td className="px-2 py-1">{p.item_name}</td>
                              <td className="px-2 py-1 text-right font-mono">${p.cost.toFixed(2)}</td>
                              <td className="px-2 py-1 text-right font-mono">${p.sell.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-base font-bold" style={{ color: "var(--success)" }}>
                <CheckCircle2 className="w-5 h-5" /> Import complete
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total rows" value={result.total_rows} />
                <Stat label="Created" value={result.created} accent />
                <Stat label="Updated" value={result.updated} accent />
              </div>
              {result.errors?.length > 0 && (
                <div className="text-xs p-3 rounded" style={{ background: "var(--danger-soft)", color: "#FECDD3", border: "1px solid rgba(251,113,133,0.30)" }}>
                  <div className="font-bold mb-1">{result.errors.length} row(s) skipped:</div>
                  <ul className="list-disc ml-5">
                    {result.errors.slice(0, 8).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
          {!result && (
            <>
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button
                onClick={() => upload(true)}
                disabled={!file || busy}
                data-testid="import-preview-btn"
                className="btn btn-secondary"
              >
                {busy ? "Checking…" : "Preview"}
              </button>
              <button
                onClick={() => upload(false)}
                disabled={!file || !preview || busy || preview?.valid === 0}
                data-testid="import-apply-btn"
                className="btn btn-primary"
              >
                <Upload className="w-3.5 h-3.5" /> Import {preview ? `(${preview.valid})` : ""}
              </button>
            </>
          )}
          {result && (
            <button
              onClick={() => { onDone?.(); onClose(); }}
              data-testid="import-close-btn"
              className="btn btn-primary"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="surface-inset p-3">
      <div className="label-eyebrow mb-1">{label}</div>
      <div
        className="font-display text-2xl font-bold"
        style={accent ? { background: "var(--gradient-accent)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" } : { color: "var(--text-primary)" }}
        data-num
      >
        {value}
      </div>
    </div>
  );
}
